import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CacheService } from 'src/app/core/services/cache/cache.service';
import { FacadeService } from 'src/app/core/services/facade.service';
import { LogService } from 'src/app/core/services/log/log.service';
import { Beneficiaire } from 'src/app/shared/models/beneficiaire';
import { NominatimResponse } from 'src/app/shared/models/api-response/nominatim-response';
import { Employee } from 'src/app/shared/models/employee/employee';
import {
	PARAM_LABEL_BENEFICIAIRE,
	PARAM_LABEL_INTERVENANT
} from 'src/app/core/services/constant/constants.service';
import { ServiceCenter } from 'src/app/shared/models/service-center';
import {
	AbstractControl,
	FormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators
} from '@angular/forms';
import { Address } from 'src/app/shared/models/address';
import { Observable, of, Subscriber } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { CareSkill } from 'src/app/shared/models/employee/care-skill';
import { Area } from 'src/app/shared/models/area';
import { CenterDepartment } from 'src/app/shared/models/center-department';

@Component({
	selector: 'app-beneficiaire-form',
	templateUrl: './beneficiaire-form.component.html',
	styleUrls: ['./beneficiaire-form.component.scss']
})
export class BeneficiaireFormComponent implements OnInit {
	/**
	 * Beneficiaire
	 */
	@Input() beneficiaire: Beneficiaire;

	/**
	 * Vrai si formulaire d'ajout, faux sinon (formulaire de détail)
	 */
	@Input() isAddForm: boolean;

	/**
	 * Addresse sélectionner dans le typeahead
	 */
	@Input() isAddressSelected: { value: boolean };

	/**
	 * Booleen indiquant si il s'agit de l'addresse par défaut ou non
	 */
	@Input() isAddressDefault: { value: boolean };

	/**
	 * Formulaire angular
	 */
	@Input() form: FormGroup;

	/**
	 * Boolean empêchant l'utilisateur d'envoyer plusieurs fois le formulaire
	 */
	@Input() isValidateButtonClicked: { value: boolean };

	/**
	 * Signal émis lors de l'envoi du formulaire pour que la sauvegarde du formulaire ce fasse dans le composant parent
	 */
	@Output() sendForm: EventEmitter<any> = new EventEmitter();

	/**
	 * Signal émis pour que la modal soit fermée
	 */
	@Output() hideModal: EventEmitter<any> = new EventEmitter();

	LABEL_BENEFICIAIRE: string;
	LABEL_INTERVENANT: string;
	MAX_NAME_CHAR = 25;

	/**
	 * Centre de l'utilisateur
	 */
	center: ServiceCenter;

	competences: CareSkill[] = [];
	selectedCompetences: CareSkill[] = [];

	/**
	 * Addresse selectionner
	 */
	selectedaddress?: Address;

	/**
	 * Typeahead des adresse en chargement
	 */
	typeaheadLoading?: boolean;

	/**
	 * Liste des adresse disponibles dans le typeahead
	 */
	addressList: Observable<NominatimResponse[]>;

	/**
	 * Liste des adresse récupérer grace au backend
	 */
	searchResults: NominatimResponse[];

	isPreferencesActive = false;
	isSESSAD = false;
	isSAAD = false;
	isSSIAD = false;
	isAreaActive = false;

	/**
	 * Liste des employées
	 */
	listEmployee: Employee[];
	listEmployeeMapped = []; // list pour le typeahead sous forme {name:nom_employee,object:employee}

	listPreferredEmployee: Employee[] = [];
	listIncompatibleEmployee: Employee[] = [];
	areaList: Area[] = [
		new Area({ id: -1, name: 'Non spécifié', description: '' })
	];

	centerDepartments: CenterDepartment[] = [];
	listSelectedDepartment: CenterDepartment[] = [];

	constructor(
		private facade: FacadeService,
		private logger: LogService,
		private cache: CacheService
	) {
		this.addressList = new Observable((observer: Subscriber<string>) => {
			// Runs on every search
			observer.next(this.form.value.address);
		}).pipe(
			mergeMap((token: string) =>
				this.getNominatimDisplaynameAsObservable(token)
			)
		);
	}

	ngOnInit(): void {
		if (this.beneficiaire) {
			this.LABEL_BENEFICIAIRE =
				this.facade.dataStorageService.getLabelCapitalized(
					PARAM_LABEL_BENEFICIAIRE
				);
			this.LABEL_INTERVENANT =
				this.facade.dataStorageService.getLabelCapitalized(
					PARAM_LABEL_INTERVENANT
				);
			this.isPreferencesActive =
				this.facade.dataStorageService.isPreferencesBeneficiaireActive();
			this.isSESSAD = this.facade.dataStorageService.isSESSAD();
			this.isSAAD = this.facade.dataStorageService.isSAAD();
			this.isSSIAD = this.facade.dataStorageService.isSSIAD();
			this.isAreaActive = this.facade.dataStorageService.isAreaActive();
			this.center =
				this.facade.dataStorageService.getUser()?.employee.serviceCenter;

			this.getAllEmployeesAndSetEmployeesInForm();

			this.address?.setValidators([
				Validators.maxLength(60),
				this.isAddressValidValidator()
			]);

			if (!this.beneficiaire.careSkills) {
				this.beneficiaire.careSkills = [];
			} else {
				this.selectedCompetences = [...this.beneficiaire.careSkills];
			}

			this.facade.careSkillService
				.getAllCareSkills()
				.subscribe((careskills) => (this.competences = careskills));

			if (this.isAreaActive) {
				this.facade.areaService.getAllAreasForUserCenter().subscribe({
					next: (areas: Area[]) => {
						this.areaList = [...this.areaList, ...areas];
					},
					error: (err) => {
						this.logger.error(err);
					}
				});
			}
			this.facade.centerDepartmentService
				.getAllCenterDepartmentsForUserCenter()
				.subscribe({
					next: (centerDepartments: CenterDepartment[]) => {
						this.centerDepartments = [...centerDepartments];
					},
					error: (err) => {
						this.logger.error(err);
					}
				});
		}
	}

	getAllEmployeesAndSetEmployeesInForm() {
		this.facade.employeeService.getAllActiveEmployees().subscribe((data) => {
			this.listEmployee = data;
			this.listEmployeeMapped = data.flatMap((e) => {
				return { name: e.firstName + ' ' + e.lastName, object: e };
			});
			if (!this.isAddForm) {
				if (this.beneficiaire.address && this.beneficiaire.address.label) {
					this.isAddressDefault.value = true;
				}

				if (
					this.beneficiaire.centerDepartments &&
					this.beneficiaire.centerDepartments.length > 0
				) {
					this.listSelectedDepartment = [
						...this.beneficiaire.centerDepartments
					];
				}
				/**
				 * Récupère les données et initialise le formulaire
				 */
				this.form.setValue({
					firstName: this.beneficiaire.firstName,
					lastName: this.beneficiaire.lastName,
					address: this.beneficiaire.address.label || '',
					email: this.beneficiaire.email || '',
					tel: this.beneficiaire.phone || '',
					preferredEmployee1: this.listEmployee.find(
						(e) => e.id === this.beneficiaire?.preferredEmployee1?.id
					)
						? this.getFullNameOfEmp(
								this.listEmployee.find(
									(e) => e.id === this.beneficiaire?.preferredEmployee1?.id
								)
						  )
						: '',
					preferredEmployee2: this.listEmployee.find(
						(e) => e.id === this.beneficiaire?.preferredEmployee2?.id
					)
						? this.getFullNameOfEmp(
								this.listEmployee.find(
									(e) => e.id === this.beneficiaire?.preferredEmployee2?.id
								)
						  )
						: '',
					preferredEmployee3: this.listEmployee.find(
						(e) => e.id === this.beneficiaire?.preferredEmployee3?.id
					)
						? this.getFullNameOfEmp(
								this.listEmployee.find(
									(e) => e.id === this.beneficiaire?.preferredEmployee3?.id
								)
						  )
						: '',
					incompatibleEmployee1: this.listEmployee.find(
						(e) => e.id === this.beneficiaire?.incompatibleEmployee1?.id
					)
						? this.getFullNameOfEmp(
								this.listEmployee.find(
									(e) => e.id === this.beneficiaire?.incompatibleEmployee1?.id
								)
						  )
						: '',
					incompatibleEmployee2: this.listEmployee.find(
						(e) => e.id === this.beneficiaire?.incompatibleEmployee2?.id
					)
						? this.getFullNameOfEmp(
								this.listEmployee.find(
									(e) => e.id === this.beneficiaire?.incompatibleEmployee2?.id
								)
						  )
						: '',
					incompatibleEmployee3: this.listEmployee.find(
						(e) => e.id === this.beneficiaire?.incompatibleEmployee3?.id
					)
						? this.getFullNameOfEmp(
								this.listEmployee.find(
									(e) => e.id === this.beneficiaire?.incompatibleEmployee3?.id
								)
						  )
						: '',
					area: this.beneficiaire.area
						? this.beneficiaire.area.name
						: 'Non spécifié'
				});
				this.listPreferredEmployee = [
					this.beneficiaire?.preferredEmployee1,
					this.beneficiaire?.preferredEmployee2,
					this.beneficiaire?.preferredEmployee3
				];
				this.listIncompatibleEmployee = [
					this.beneficiaire?.incompatibleEmployee1,
					this.beneficiaire?.incompatibleEmployee2,
					this.beneficiaire?.incompatibleEmployee3
				];
			}
		});
	}

	getFullNameOfEmp(emp: Employee) {
		return emp.firstName + ' ' + emp.lastName;
	}

	/**
	 * Méthode appeler lors de la sélection d'un élément dans le typeahead
	 * @param event
	 */
	onAddressSelect(event: TypeaheadMatch) {
		this.isAddressDefault.value = false;
		this.isAddressSelected.value = true;
		this.selectedaddress = new Address();
		this.selectedaddress.label = event.item.displayName;
		this.selectedaddress.latitude = event.item.latitude;
		this.selectedaddress.longitude = event.item.longitude;
		this.form.controls['address'].updateValueAndValidity();
	}

	getNominatimDisplaynameAsObservable(
		token: string
	): Observable<NominatimResponse[]> {
		this.isAddressSelected.value = false;
		this.facade.nominatimService.addressLookup(token).subscribe({
			next: (result: any) => {
				this.searchResults = result.features.map(
					(feature) =>
						new NominatimResponse(
							feature.geometry.coordinates[0],
							feature.geometry.coordinates[1],
							feature.properties.label
						)
				);
			},
			error: (err) => {
				this.logger.log(
					'' + err.status + ' ' + err.error.response ? err.error.response : ''
				);
			}
		});
		return of(this.searchResults);
	}

	changeTypeaheadLoading(e: boolean): void {
		this.typeaheadLoading = e;
	}

	/**
	 * Fonction appelée si une adresse commence à être tapée.
	 */
	selectedAddressKeyUp(e: any) {
		this.isAddressDefault.value = false;
		if (this.selectedaddress) {
			if (this.selectedaddress?.label === e.value) {
				this.isAddressSelected.value = true;
			}
		}
		this.form.controls['address'].updateValueAndValidity();
	}

	isDepartmentCheck(department: CenterDepartment): boolean {
		return this.listSelectedDepartment?.some((dpt) => dpt.id === department.id);
	}

	updateSelectedDepartment(e: any, department: CenterDepartment) {
		if (e.target.checked) {
			if (!this.listSelectedDepartment.some((z) => z.id === department.id)) {
				this.listSelectedDepartment.push(department);
			}
		} else {
			if (this.listSelectedDepartment.some((z) => z.id === department.id)) {
				this.listSelectedDepartment = this.listSelectedDepartment.filter(
					(z) => z.id !== department.id
				);
			}
		}
	}

	/**
	 * Méthode d'envoie de formulaire, si celui est valide stock les données et envoie un signal au composant parent
	 */
	submitForm() {
		if (this.form.valid) {
			this.beneficiaire.firstName = this.form.value.firstName;
			this.beneficiaire.lastName = this.form.value.lastName;
			this.beneficiaire.email = this.form.value.email;
			this.beneficiaire.phone = this.form.value.tel;
			if (this.isAddressSelected.value && this.selectedaddress !== null) {
				this.beneficiaire.address = this.selectedaddress;
			} else if (!this.isAddressDefault.value) {
				this.beneficiaire.address.label = this.form.value.address;
			}
			if (this.isSESSAD) {
				// this.beneficiaire.careSkill.name = this.form.value.competences;
				this.beneficiaire.careSkills = [...this.selectedCompetences];
			} else {
				// sinon "DalCareSkill.findByName : can't find care skill" renvoyé par le back car pas de care skill
				// (avec planificatrice SAAD)
				this.beneficiaire.careSkills = [];
			}
			this.beneficiaire.serviceCenter = this.center;

			if (this.listPreferredEmployee.length > 0) {
				this.beneficiaire.preferredEmployee1 =
					this.listPreferredEmployee[0] ?? null;
				this.beneficiaire.preferredEmployee2 =
					this.listPreferredEmployee[1] ?? null;
				this.beneficiaire.preferredEmployee3 =
					this.listPreferredEmployee[2] ?? null;
			}
			if (this.listIncompatibleEmployee.length > 0) {
				this.beneficiaire.incompatibleEmployee1 =
					this.listIncompatibleEmployee[0] ?? null;
				this.beneficiaire.incompatibleEmployee2 =
					this.listIncompatibleEmployee[1] ?? null;
				this.beneficiaire.incompatibleEmployee3 =
					this.listIncompatibleEmployee[2] ?? null;
			}

			this.beneficiaire.area = null;
			if (this.isAreaActive) {
				if (this.form.value.area !== 'Non spécifié') {
					this.beneficiaire.area = this.areaList.find((area) => {
						return area.name === this.form.value.area;
					});
				}
			}

			this.beneficiaire.centerDepartments = this.listSelectedDepartment;

			this.sendForm.emit();
		}
	}

	getCompetenceLabel(competence: CareSkill) {
		return competence.name.replaceAll('_', ' ');
	}

	get firstName() {
		return this.form?.get('firstName');
	}
	get lastName() {
		return this.form?.get('lastName');
	}
	get address() {
		return this.form?.get('address');
	}
	get preferredEmployee1() {
		return this.form?.get('preferredEmployee1');
	}
	get preferredEmployee2() {
		return this.form?.get('preferredEmployee2');
	}
	get preferredEmployee3() {
		return this.form?.get('preferredEmployee3');
	}
	get incompatibleEmployee1() {
		return this.form?.get('incompatibleEmployee1');
	}
	get incompatibleEmployee2() {
		return this.form?.get('incompatibleEmployee2');
	}
	get incompatibleEmployee3() {
		return this.form?.get('incompatibleEmployee3');
	}
	get area() {
		return this.form?.get('area');
	}
	get tel() {
		return this.form?.get('tel');
	}
	get email() {
		return this.form?.get('email');
	}
	get department() {
		return this.form?.get('department');
	}

	isAddressValidValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			// Champ vide → valide
			if (!control.value || control.value.trim() === '') {
				return null;
			}
			// Champ non vide → on vérifie que l'adresse est sélectionnée ou par défaut
			if (this.isAddressSelected.value || this.isAddressDefault.value) {
				return null;
			}
			// Adresse renseignée mais non validée → invalide
			return { invalidAddress: { value: control.value } };
		};
	}

	typeaheadEmployeeOnSelect(
		event: TypeaheadMatch,
		prefOrIncompat: string,
		position: number
	): void {
		if (prefOrIncompat && prefOrIncompat === 'pref') {
			const employeeToSet = event.item.object as Employee;
			if (
				this.listPreferredEmployee.findIndex(
					(employee) => employee && employee.id === employeeToSet.id
				) === -1 &&
				this.listIncompatibleEmployee.findIndex(
					(employee) => employee && employee.id === employeeToSet.id
				) === -1
			) {
				this.listPreferredEmployee[position - 1] = employeeToSet;
				if (position === 1) {
					this.form.patchValue(
						{ preferredEmployee1: event.item.name },
						{ onlySelf: true }
					);
				} else if (position === 2) {
					this.form.patchValue(
						{ preferredEmployee2: event.item.name },
						{ onlySelf: true }
					);
				} else if (position === 3) {
					this.form.patchValue(
						{ preferredEmployee3: event.item.name },
						{ onlySelf: true }
					);
				}
			} else {
				this.logger.log('emp n°' + employeeToSet.id + ' déjà dans une liste');
				if (position === 1) {
					this.form.patchValue(
						{ preferredEmployee1: null },
						{ onlySelf: true }
					);
				} else if (position === 2) {
					this.form.patchValue(
						{ preferredEmployee2: null },
						{ onlySelf: true }
					);
				} else if (position === 3) {
					this.form.patchValue(
						{ preferredEmployee3: null },
						{ onlySelf: true }
					);
				}
			}
		} else if (prefOrIncompat && prefOrIncompat === 'incompat') {
			const employeeToSet = event.item.object as Employee;
			if (
				this.listPreferredEmployee.findIndex(
					(employee) => employee && employee.id === employeeToSet.id
				) === -1 &&
				this.listIncompatibleEmployee.findIndex(
					(employee) => employee && employee.id === employeeToSet.id
				) === -1
			) {
				this.listIncompatibleEmployee[position - 1] = employeeToSet;
				if (position === 1) {
					this.form.patchValue(
						{ incompatibleEmployee1: event.item.name },
						{ onlySelf: true }
					);
				} else if (position === 2) {
					this.form.patchValue(
						{ incompatibleEmployee2: event.item.name },
						{ onlySelf: true }
					);
				} else if (position === 3) {
					this.form.patchValue(
						{ incompatibleEmployee3: event.item.name },
						{ onlySelf: true }
					);
				}
			} else {
				this.logger.log('emp n°' + employeeToSet.id + ' déjà dans une liste');
				if (position === 1) {
					this.form.patchValue(
						{ incompatibleEmployee1: null },
						{ onlySelf: true }
					);
				} else if (position === 2) {
					this.form.patchValue(
						{ incompatibleEmployee2: null },
						{ onlySelf: true }
					);
				} else if (position === 3) {
					this.form.patchValue(
						{ incompatibleEmployee3: null },
						{ onlySelf: true }
					);
				}
			}
		}
	}

	employeeKeyUp(e: any, prefOrIncompat: string, position: number) {
		if (prefOrIncompat && prefOrIncompat === 'pref') {
			if (
				this.listPreferredEmployee[position - 1] &&
				this.listPreferredEmployee[position - 1].firstName +
					' ' +
					this.listPreferredEmployee[position - 1].lastName !==
					e.value
			) {
				this.listPreferredEmployee[position - 1] = null;
			}
		} else if (prefOrIncompat && prefOrIncompat === 'incompat') {
			if (
				this.listIncompatibleEmployee[position - 1] &&
				this.listIncompatibleEmployee[position - 1].firstName +
					' ' +
					this.listIncompatibleEmployee[position - 1].lastName !==
					e.value
			) {
				this.listIncompatibleEmployee[position - 1] = null;
			}
		}
	}

	isCompetenceCheck(comp: CareSkill): boolean {
		return this.selectedCompetences?.some(
			(currComp) => currComp.id === comp.id
		);
	}

	updateSelectedCompetence(e: any, comp: CareSkill) {
		if (e.target.checked) {
			if (!this.selectedCompetences?.some((z) => z.id === comp.id)) {
				this.selectedCompetences.push(comp);
			}
		} else if (this.selectedCompetences?.some((z) => z.id === comp.id)) {
			this.selectedCompetences = this.selectedCompetences.filter(
				(z) => z.id !== comp.id
			);
		}
	}
}
